import {Component, effect, inject, input, signal} from "@angular/core";
import {AssetComponent} from "src/app/presentation/common/asset.component";
import {Animal, Asset, DeviceAssociation} from "src/app/infrastructure/moovement-api/models/asset";
import {DataSubscriptionService} from "src/app/infrastructure/data-subscription.service";
import {oneHour} from "src/app/presentation/common/globals";
import {Samples} from "src/app/infrastructure/moovement-api/models/samples";
import {DataSynchronisationService} from "src/app/infrastructure/data-synchronisation.service";
import {getValueByKeyMatching, isEqualToOrYoungerThanOffset} from "src/app/app.config";
import {ViewportService} from "src/app/infrastructure/viewport.service";
import {takeUntil} from "rxjs";
import {NetworkStatusStore} from "src/app/infrastructure/network/network-status.store";
import {timeAgo} from 'src/app/domain/util/timestamps/timestamps';
import {AggregatedAssetStateStore} from "src/app/domain/assets/aggregated-state/aggregated-asset-state.store";
import {randomUuid} from "src/app/domain/util/uuid/uuid";
import {batteryIcon, batteryLevelColor} from "src/app/domain/assets/battery/battery";
import {DeviceAssociationStore} from "src/app/domain/devices/device-association.store";
import {UserStore} from "src/app/domain/user/user.store";

export const animalMidVoltageThreshold = 3.91
export const animalLowVoltageThreshold = 2.00

@Component({
  template: ``,
  standalone: false
})
export abstract class AssetDetailsComponent<A extends Asset> extends AssetComponent {

  id = input.required<string>();

  focusable: boolean = true;

  statusMessage = signal('Last seen: ?');
  statusMessageColorClass = signal('color-danger');

  asset = signal<A | undefined>(undefined);

  protected readonly samplesKey = signal<string | undefined>(undefined)

  protected lastSeenByBLEScanner = signal<Date | undefined>(undefined)
  protected isLastSeenWithinThreshold = signal(false);

  protected deviceAssociations = signal<DeviceAssociation[]>([]);

  protected batteryMidThreshold: number = animalMidVoltageThreshold
  protected batteryLowThreshold: number = animalLowVoltageThreshold
  protected batteryVoltage = signal<number | undefined>(undefined)
  protected batterLevelColor = signal('color-primary-information');
  protected batteryIcon = signal('fa-solid fa-battery-empty');
  protected canBeAltered: boolean = false;
  protected canBeDeleted: boolean = false;
  protected allAssetSamples = signal<Map<A, Array<Samples.AssetSample<any>>>> (new Map())
  protected allCurrentAssetsSamples = signal<Array<Samples.AssetSample<any>>>([])
  protected lastAssetSample = signal<Samples.AssetSample<any> | undefined>(undefined)
  protected lastBatteryVoltageAssetSample?: Samples.AssetSample<number>

  protected readonly aggregatedAssetStateStore = inject(AggregatedAssetStateStore);
  protected readonly deviceAssociationStore = inject(DeviceAssociationStore);
  protected readonly networkStatusStore = inject(NetworkStatusStore);

  private userStore = inject(UserStore);

  protected constructor(
    dataSynchronisationService: DataSynchronisationService,
    dataSubscriptionService: DataSubscriptionService,
    viewPortService: ViewportService
  ) {
    super(dataSynchronisationService, dataSubscriptionService, viewPortService);

    effect(() => {
      this.canBeAltered = this.userStore.canEditPersistable(this.asset());
      this.canBeDeleted = this.userStore.canDeletePersistable(this.asset());
    });

    effect(() => {
      const lastAssetSample = this.allCurrentAssetsSamples().filter(this.lastAssetSampleFilter).at(0);
      this.lastAssetSample.set(lastAssetSample);
      this.isLastSeenWithinThreshold.set(this.lastSeenWithinThreshold(this.allCurrentAssetsSamples()));
    });

    effect(() => {
      this.createMessage();
    });

    effect(() => {
      const assetId = this.id();
      if (!assetId) return;

      const aggregatedAssetState = this.aggregatedAssetStateStore.entityMap()[assetId]
      if (!aggregatedAssetState) return;

      this.lastSeenByBLEScanner.set(aggregatedAssetState?.lastSeenByBLEScannerAt);
      this.batteryVoltage.set(aggregatedAssetState.batteryVoltage);
      this.batterLevelColor.set(batteryLevelColor(aggregatedAssetState.batteryVoltage, this.batteryLowThreshold, this.batteryMidThreshold));
      this.batteryIcon.set(batteryIcon(aggregatedAssetState.batteryVoltage, this.batteryLowThreshold, this.batteryMidThreshold));
    });

    effect(() => {
      const relatedDeviceAssociations = this.deviceAssociationStore.assetIdToAssocations().get(this.id()) || [];
      this.deviceAssociations.set(relatedDeviceAssociations);
    });

    effect(() => {
      const samplesKey = this.samplesKey();
      if (!samplesKey) return;

      this.dataSubscriptionService.onNext<Map<A, Array<Samples.AssetSample<any>>>>(samplesKey)
        .pipe(takeUntil(this.destroy$))
        .subscribe(assetSamples => this.allAssetSamples.set(assetSamples));
    });

    effect(() => {
      this.allCurrentAssetsSamples.set(getValueByKeyMatching(this.allAssetSamples(), asset => asset.id == this.id()) || [])
    });

    effect(() => {
      if (!this.focusable) return;

      const assetId = this.id();
      if (!assetId) return;

      this.dataSubscriptionService.publish(assetId + '.focus', randomUuid())
    });
  }

  protected lastAssetSampleFilter: (as: Samples.AssetSample<any>) => boolean = it => true

  protected createMessage() {
    const lastAssetSample = this.lastAssetSample();
    if (!lastAssetSample) return;

    this.statusMessage.set(`Last seen: ${timeAgo(new Date(lastAssetSample.sample!.instant!))}`);

    const isSeenWithinThreshold = this.isLastSeenWithinThreshold();
    if (isSeenWithinThreshold) {
      this.statusMessageColorClass.set("color-primary-information");
      return;
    }

    this.statusMessageColorClass.set("color-danger");
  }

  protected getAssetName(): string {
    const asset = this.asset();
    if (asset?.name) {
      return asset?.name
    }
    if ((asset as Animal)?.visualTag != undefined) {
      return (asset as any).visualTag
    }
    return '?'
  }

  protected override close() {
    super.close();
  }

  protected lastAssetSampleBy(filter: (assetSample: Samples.AssetSample<any>) => boolean) {
    return this.allCurrentAssetsSamples().filter(filter)?.at(0)
  }

  protected toggleCustomize(url: string): void {
    if (!this.loading && this.canBeAltered) {
      this.router.navigate([url, this.id()]);
    }
  }

  protected lastSeenWithinThreshold(assetSamples: Samples.AssetSample<any>[]) {
    return lastSeenWithinThreshold(assetSamples);
  }
}

export function lastSeenWithinThreshold(assetSamples: Samples.AssetSample<any>[], capability: string = 'DeviceHeartbeat') {
  if (capability) assetSamples = assetSamples?.filter(assetSample => assetSample.sample?.capability === capability);

  const instant = assetSamples?.at(0)?.sample?.instant
  if (!instant) return false;

  return isEqualToOrYoungerThanOffset(new Date(instant), oneHour);
}
